import { useContext, useEffect, useMemo, useState } from 'react';
import { DataContext } from '@/components/context/DataContext';
import { socket } from '@/services/socket';
import { ctaEquityTickers } from 'lib/constants';
import { getIrsCta, getForexCtaTickers } from '@/services/cta.service';

// Pull 3-letter currency codes out of a 3- or 6-char pair ticker.
const splitPair = (t) => {
    const s = String(t || '').toUpperCase();
    if (s.length === 6) return [s.slice(0, 3), s.slice(3)];
    if (s.length === 3) return [s];
    return [];
};

export default function useCtascanCatalog() {
    const ctx = useContext(DataContext) || {};
    const ratesHighlights = ctx.ratesHighlights;

    const [irsCurves, setIrsCurves] = useState([]);
    const [sectorsFromCta, setSectorsFromCta] = useState([]);
    const [fxCurrencies, setFxCurrencies] = useState(() => new Set());

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await getIrsCta({ debug: false });
                if (cancelled || !resp?.status) return;
                const entity = resp?.data?.entity || {};
                const nextSectors = Object.keys(entity);
                const nextCurves = [];
                Object.keys(entity).forEach((sector) => {
                    (entity[sector] || []).forEach((row) => {
                        if (!row || !row.curve) return; // only explicit curve identifiers
                        nextCurves.push({ name: row.curve, ccy: row.ccy, irsId: row.irsId, sector });
                    });
                });
                setSectorsFromCta(nextSectors);
                setIrsCurves(nextCurves);
            } catch (e) {
                console.warn('[ctascan] catalog load failed', e);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // ---- FX currencies: load the catalog from /cta/forex/tickers, then
    // ---- augment with Currency hits seen on the /global-search socket. ---
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await getForexCtaTickers();
                if (cancelled || !resp?.status) return;
                const ccys = new Set();
                (resp?.data?.entity || []).forEach((r) => {
                    splitPair(r?.fxTicker).forEach((c) => ccys.add(c));
                });
                if (!ccys.size) return;
                setFxCurrencies((prev) => {
                    const next = new Set(prev);
                    let changed = false;
                    ccys.forEach((c) => { if (!next.has(c)) { next.add(c); changed = true; } });
                    return changed ? next : prev;
                });
            } catch (e) {
                console.warn('[ctascan] fx tickers load failed', e);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const onGlobalSearch = (payload) => {
            const results = payload?.results;
            if (!Array.isArray(results)) return;
            const seen = new Set();
            results.forEach((g) => {
                (g.items || []).forEach((it) => {
                    if (it?.type === 'Currency' && typeof it.ticker === 'string') {
                        splitPair(it.ticker).forEach((c) => seen.add(c));
                    }
                });
            });
            if (!seen.size) return;
            setFxCurrencies((prev) => {
                let changed = false;
                const next = new Set(prev);
                seen.forEach((c) => { if (!next.has(c)) { next.add(c); changed = true; } });
                return changed ? next : prev;
            });
        };
        socket.on('/global-search', onGlobalSearch);
        return () => socket.off('/global-search', onGlobalSearch);
    }, []);

    // ---- Sectors: prefer the freshly-fetched list, then DataContext.
    const sectors = useMemo(() => {
        if (sectorsFromCta.length) return sectorsFromCta;
        if (Array.isArray(ratesHighlights) && ratesHighlights.length) {
            const fromCta = ratesHighlights.map((s) => s?.header).filter(Boolean);
            if (fromCta.length) return fromCta;
        }
        return [];
    }, [sectorsFromCta, ratesHighlights]);

    return useMemo(() => ({
        irsCurves,
        equityTickers: ctaEquityTickers,
        fxCurrencies,
        sectors
    }), [irsCurves, fxCurrencies, sectors]);
}
